import { INote } from "../components/dashboard/knowledge-bases-create/NoteForm";
import { createRandomString, isAlphabetic } from "../components/tools";

const removeEmptyStrings = (obj: object): object => {
  // Handle different object types: null, undefined, arrays, and plain objects
  if (obj === null || obj === undefined) {
    return obj;
  } else if (Array.isArray(obj)) {
    return obj.filter((item) => removeEmptyStrings(item)); // Recursively filter array elements
  } else if (typeof obj === "object") {
    // Iterate over object keys and create a new object
    return Object.keys(obj).reduce((acc, key) => {
      const value = removeEmptyStrings(obj[key]); // Recursively remove empty strings
      // Add key-value pair to accumulator only if value is not an empty string
      if (
        (value !== undefined && typeof value !== "string") ||
        value.trim() !== ""
      ) {
        acc[key] = value;
      }
      return acc;
    }, {});
  }
  // Return non-object values and empty arrays/objects as-is
  return obj;
};

const VoiceCommandAudioLinks: any = {
  en: "https://cdn.1cdn.app/application/LOUISEBOT/24011513533787_response_en.mp3",
  fr: "https://cdn.1cdn.app/application/LOUISEBOT/24011513533739_response_fr.mp3"
};

const alwaysShowButtonsAudioLinks: any = {
  en: "https://cdn.1cdn.app/application/LOUISEBOT/24050405583256_deplay_btn_en.mp3",
  fr: "https://cdn.1cdn.app/application/LOUISEBOT/24050405583275_deplay_btn_fr.mp3"
};

const VoiceResponseAudioLinks: any = {
  en: "https://cdn.1cdn.app/application/LOUISEBOT/24050405571724_answer_by_voice_en.mp3",
  fr: "https://cdn.1cdn.app/application/LOUISEBOT/24050405571755_answer_by_voice_fr.mp3"
};
const VoiceNotificationBadgeAudioLinks: any = {
  en: "https://cdn.1cdn.app/application/LOUISEBOT/24081908010669_notofication_badge_en.mp3",
  fr: "https://cdn.1cdn.app/application/LOUISEBOT/24081908010642_notofication_badge_fr.mp3"
};

const VoiceNotificationMessageAudioLinks: any = {
  en: "https://cdn.1cdn.app/application/LOUISEBOT/24081409571014_notification_message_voice_en.mp3",
  fr: "https://cdn.1cdn.app/application/LOUISEBOT/24081409571162_notification_message_voice_fr.mp3"
};

const VoiceNotificationSoundAudioLinks: any = {
  en: "https://cdn.1cdn.app/application/LOUISEBOT/25010914403384_sound_notification_en.mp3",
  fr: "https://cdn.1cdn.app/application/LOUISEBOT/25010914403373_sound_notification_fr.mp3"
};

const VoiceTrack: any = {
  en: "https://cdn.1cdn.app/application/LOUISEBOT/25011811475233_conversations_en.mp3",
  fr: "https://cdn.1cdn.app/application/LOUISEBOT/25011811475241_conversations_fr.mp3"
};

const VoiceSaveStatsTrack: any = {
  en: "https://cdn.1cdn.app/application/LOUISEBOT/25011811475217_statistics_en.mp3",
  fr: "https://cdn.1cdn.app/application/LOUISEBOT/25011811475283_statistics_fr.mp3"
};
const VoiceAccessWithoutPasswordTooltip: any = {
  en: "https://cdn.1cdn.app/application/LOUISEBOT/25011813262843_passless_en.mp3",
  fr: "https://cdn.1cdn.app/application/LOUISEBOT/25011813262932_passless_fr.mp3"
};

const VoiceNotificationEnableSeo: any = {
  en: "https://cdn.1cdn.app/application/LOUISEBOT/25012509555088_clone_tootips_en.mp3",
  fr: "https://cdn.1cdn.app/application/LOUISEBOT/25012509555127_clone_tootips_fr.mp3"
};

const VoiceResponseSound: {
  language: string;
  gender: string;
  voice: string;
  cdnPath: string;
}[] = [
  {
    language: "en",
    gender: "female",
    voice: "DEFAULT",
    cdnPath:
      "https://cdn.1cdn.app/application/LOUISEBOT/24072309250381_female_voice_preview_en.mp3"
  },
  {
    language: "fr",
    gender: "female",
    voice: "DEFAULT",
    cdnPath:
      "https://cdn.1cdn.app/application/LOUISEBOT/2407230925031_female_voice_preview_fr.mp3"
  },
  {
    language: "en",
    gender: "male",
    voice: "MIKE",
    cdnPath:
      "https://cdn.1cdn.app/application/LOUISEBOT/24072309250325_male_voice_preview_en.mp3"
  },
  {
    language: "fr",
    gender: "male",
    voice: "MIKE",
    cdnPath:
      "https://cdn.1cdn.app/application/LOUISEBOT/24072309250364_male_voice_preview_fr.mp3"
  },
  {
    language: "en",
    gender: "female",
    voice: "SALLY",
    cdnPath:
      "https://cdn.1cdn.app/application/LOUISEBOT/2411112224386_female_sally.mp3"
  },
  {
    language: "fr",
    gender: "female",
    voice: "SALLY",
    cdnPath:
      "https://cdn.1cdn.app/application/LOUISEBOT/24111122310670_female_sally_fr.mp3"
  },
  {
    language: "en",
    gender: "female",
    voice: "EMILY",
    cdnPath:
      "https://cdn.1cdn.app/application/LOUISEBOT/2411112224387_female_emily.mp3"
  },
  {
    language: "fr",
    gender: "female",
    voice: "EMILY",
    cdnPath:
      "https://cdn.1cdn.app/application/LOUISEBOT/24111122310572_female_emily_fr.mp3"
  },
  {
    language: "en",
    gender: "female",
    voice: "AVA",
    cdnPath:
      "https://cdn.1cdn.app/application/LOUISEBOT/2411112224399_female_ava.mp3"
  },
  {
    language: "fr",
    gender: "female",
    voice: "AVA",
    cdnPath:
      "https://cdn.1cdn.app/application/LOUISEBOT/24111122310578_female_ava_fr.mp3"
  }
];

function checkImageMimeType(file: any) {
  const imageMimeTypes = [
    "image/jpeg",
    "image/png",
    "image/gif",
    "image/bmp",
    "image/webp"
  ];

  if (file && imageMimeTypes.includes(file.type)) {
    return true;
  }
  return false;
}

function checkVideoMimeType(file: any) {
  const mimeType = file.type;
  const keywords = ["video"];

  // Remove the quotes from the string if necessary
  const cleanedMimeType = mimeType.replace(/['"]+/g, "");

  // Check if any keyword in the list is present in the mimeType
  const containsKeyword = keywords.some((keyword) =>
    cleanedMimeType.includes(keyword)
  );

  return containsKeyword;
}

function checkFileSize(size: number, file: any) {
  const maxSize = size * 1024 * 1024; // 30 MB in bytes
  if (file && file.size > maxSize) {
    return false;
  } else {
    return true;
  }
}

enum Video {
  MP4 = "video/mp4",
  MPEG = "video/mpeg",
  WEBM = "video/webm",
  OGG = "video/ogg",
  MOV = "video/mov",
  X_SGI = "video/x-sgi-movie",
  QUICK_TIME = "video/quicktime",
  M4V = "video/m4v",
  X_M4V = "video/x-m4v",
  X_MS = "video/x-msvideo",
  X_F4V = "video/x-f4v",
  X_MS_WMV = "video/x-ms-wmv",
  MKV = "video/mkv",
  X_MATROSKA = "video/x-matroska",
  AVI = "video/avi",
  AVI_3GPP2 = "video/3ggp2",
  AVI_3GPP = "video/3ggp",
  X_MS_ASF = "video/x-ms-asf",
  GP_3GP = "video/3gp",
  GP_3GPP = "video/3gpp",
  video = "video",
  Other = "application/octet-stream"
}

enum Image {
  JPEG = "image/jpeg",
  JPG = "image/jpg",
  PNG = "image/png",
  GIF = "image/gif"
}

enum Icon {
  JPEG = "image/jpeg",
  JPG = "image/jpg",
  PNG = "image/png",
  GIF = "image/gif",

  MP4 = "video/mp4",
  WEBM = "video/webm",
  OGG = "video/ogg",
  MOV = "video/mov"
}

enum File {
  DOCUMENT = "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  JSON = "application/json",
  PDF = "application/pdf",
  PRESENTATION = "application/vnd.openxmlformats-officedocument.presentationml.presentation",
  TEXT = "text/plain",
  HTML = "text/html"
}

const MimeType = {
  Video,
  Image,
  Icon,
  File
};

interface ValidationSuccess {
  is_valid: true;
  value: Record<string, any>;
}

interface ValidationError {
  is_valid: false;
  value: null;
}

type ValidationResult = ValidationSuccess | ValidationError;

function isSupportedVideoFormatMimeType(mimeType: string): boolean {
  return Object.values(Video).includes(mimeType as Video);
}

function isSupportedVideoFormatWithFileName(fileName: string): boolean {
  const extension = fileName.split(".").pop(); // Get file extension
  const enumValues = Object.values(Video); // Get all enum values
  return enumValues.some((value) => value.includes(extension as Video));
}

const createBlockId = (text: string, tree: any[]) => {
  let titleCode = 0;
  tree
    .filter((t) => t.parent)
    .forEach((element) => {
      if (element.block_id) {
        const _code = element.block_id.split("-");
        if (_code[0] && Number(_code[0]) > titleCode) titleCode = +_code[0];
      }
    });

  if (typeof text !== "string" || !text) {
    return "";
  }
  let acronym = text
    ?.match(/[\p{Alpha}\p{Nd}]+/gu)
    ?.reduce(
      (previous, next) =>
        previous +
        (+next === 0 || parseInt(next) ? parseInt(next) : next[0] || ""),
      ""
    )
    ?.toUpperCase();

  if (!acronym || (acronym && acronym.length < 3) || !isAlphabetic(acronym)) {
    acronym = createRandomString(5);
  }

  return `${titleCode + 1}-${acronym?.slice(0, 3)}`;
};

const PartnetBannerLinks = {
  toolkitBox:
    "https://cdn.1cdn.app/application/LOUISEBOT/25012813160357_Box.svg",
  alexPlaceholder:
    "https://cdn.1cdn.app/application/LOUISEBOT/25012813263175_louise_BP_Alex.png",
  alexLink:
    "https://louisebot.com/en-US/chat/d7609aa8-6ff5-4897-908e-a4ddb574d1c4"
};

/**
 * checkAndReturnValidJsonWithVariables
 *
 * This method validates the input JSON string and processes any variables in the format $:var_*!.
 * It ensures proper JSON structure and adds quotes around unquoted variables.
 *
 * @param {string} inputString - The JSON string to validate and process
 * @returns {Promise<ValidationResult>} An object containing:
 *   - is_valid: boolean - Indicates if the JSON is valid
 *   - value: Record<string, any> | null - The parsed JSON object if valid, null otherwise
 *
 * @example
 * const input = `{
 *   "name": "Hello $:var_name!",
 *   "age": $:var_age!
 * }`;
 * const result = await checkAndReturnValidJsonWithVariables(input);
 * if (result.is_valid) {
 *   console.log(result.value);
 * }
 *
 * @throws {never} This method doesn't throw exceptions, errors are handled in the return value
 * @author Milad A
 * @since 2025-01-28
 */
function checkAndReturnValidJsonWithVariables(
  inputString: string
): ValidationResult {
  if (typeof inputString !== "string") {
    return {
      is_valid: false,
      value: null
    };
  }

  try {
    const jsonString = inputString.replace(
      /:\s*(\$:var_[a-zA-Z0-9_]+!)(?=\s*[,}])/g,
      ': "$1"'
    );

    const jsonObject = JSON.parse(jsonString);

    if (typeof jsonObject !== "object" || jsonObject === null) {
      return {
        is_valid: false,
        value: null
      };
    }

    return {
      is_valid: true,
      value: jsonObject
    };
  } catch (error) {
    return {
      is_valid: false,
      value: null
    };
  }
}

const setBlockNotes = (block: any) => {
  const { notes } = block;
  var newNotes = notes?.filter((x: INote) => x.title != "");
  if (newNotes?.length > 0) block.notes = newNotes;
  else delete block.notes;
  return block;
};

const Utilities = {
  removeEmptyStrings,
  VoiceCommandAudioLinks,
  alwaysShowButtonsAudioLinks,
  VoiceResponseAudioLinks,
  VoiceTrack,
  VoiceSaveStatsTrack,
  VoiceResponseSound,
  VoiceAccessWithoutPasswordTooltip,
  MimeType,
  checkImageMimeType,
  checkFileSize,
  checkVideoMimeType,
  VoiceNotificationBadgeAudioLinks,
  VoiceNotificationSoundAudioLinks,
  VoiceNotificationMessageAudioLinks,
  VoiceNotificationEnableSeo,
  isSupportedVideoFormatWithFileName,
  isSupportedVideoFormatMimeType,
  createBlockId,
  PartnetBannerLinks,
  checkAndReturnValidJsonWithVariables,
  setBlockNotes
};

export default Utilities;
